import React from 'react';
import SEOHead from './SEOHead';
import { SITE_URL, absoluteUrl, getSpecialtySeoImagePath } from '../utils/seo';

export function DoctorSEO({ doctor, specialty, city, state, slug, seoDescription }) {
  if (!doctor) return null;

  const name = doctor.full_name || '';
  const specName = specialty || doctor.specialty_name || '';
  const cityName = city || doctor.city || '';
  const stateName = state || doctor.state || '';
  const phone = doctor.phone || '';
  const address = doctor.address || '';
  const rating = doctor.rating_avg || 0;
  const reviewCount = doctor.rating_count || 0;
  const hasTrustedRating = reviewCount >= 3 && rating > 0;
  const pageSlug = slug || doctor.slug || '';
  const pagePath = pageSlug ? `/${pageSlug}` : `/medico/${doctor.id}`;
  const pageUrl = `${SITE_URL}${pagePath}`;
  const imagePath = getSpecialtySeoImagePath(specName);
  const imageUrl = absoluteUrl(imagePath);

  const pageTitle = specName && cityName
    ? `${name} | ${specName} em ${cityName} | BuscaDoutor`
    : `${name} | BuscaDoutor`;

  const pageDescription = seoDescription
    || `Veja informações profissionais, especialidade, localização e dados de atendimento de ${name}${specName ? `, ${specName}` : ''}${cityName ? ` em ${cityName}` : ''} no BuscaDoutor.`;

  const addressSchema = (address || cityName) ? {
    '@type': 'PostalAddress',
    ...(address && { streetAddress: address }),
    ...(cityName && { addressLocality: cityName }),
    ...(stateName && { addressRegion: stateName }),
    addressCountry: 'BR',
  } : null;

  const ratingSchema = hasTrustedRating ? {
    '@type': 'AggregateRating',
    ratingValue: rating.toFixed(1),
    reviewCount,
    bestRating: '5',
    worstRating: '1',
  } : null;

  const physicianSchema = {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    name,
    image: imageUrl,
    ...(specName && { medicalSpecialty: specName }),
    url: pageUrl,
    description: pageDescription.slice(0, 500),
    ...(phone && { telephone: phone }),
    ...(cityName && {
      areaServed: [
        { '@type': 'City', name: cityName },
        ...(stateName ? [{ '@type': 'State', name: stateName }] : []),
      ],
    }),
    ...(addressSchema && { address: addressSchema }),
    ...(ratingSchema && { aggregateRating: ratingSchema }),
  };

  const businessSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name,
    url: pageUrl,
    image: imageUrl,
    description: pageDescription.slice(0, 500),
    ...(phone && { telephone: phone }),
    ...(addressSchema && { address: addressSchema }),
    ...(ratingSchema && { aggregateRating: ratingSchema }),
  };

  return (
    <SEOHead
      title={pageTitle}
      description={pageDescription}
      canonicalUrl={pagePath}
      imageUrl={imagePath}
      structuredData={[physicianSchema, businessSchema]}
    />
  );
}
